import { useEffect } from 'react';
import { useQuery, useQueryClient, type QueryKey } from '@tanstack/react-query';
import type { ZodType } from 'zod';

import { subscribe, readOnce, FirebaseError } from '@/lib/firebaseClient';
import { STALE_TIMES } from '@/lib/queryClient';

/* =====================================================================
   useFirebaseValue — hook genérico para leer un path de RTDB.

   Patrón:
   - Primera carga: readOnce → TanStack Query con cache.
   - Suscripción onValue corre en paralelo y actualiza la cache cuando
     RTDB notifica cambios. Esto da lo mejor de los dos mundos:
     · Cache compartida (varios componentes consumen la misma query)
     · Refresh push (sin polling)
===================================================================== */

interface UseFirebaseValueOptions {
  enabled?: boolean;
  staleTime?: number;
}

export function useFirebaseValue<T>(
  queryKey: QueryKey,
  path: string,
  schema: ZodType<T>,
  options: UseFirebaseValueOptions = {},
) {
  const { enabled = true, staleTime = STALE_TIMES.MEDIUM } = options;
  const queryClient = useQueryClient();

  const result = useQuery<T, FirebaseError>({
    queryKey,
    queryFn: () => readOnce(path, schema),
    enabled,
    staleTime,
  });

  useEffect(() => {
    if (!enabled) return;
    const unsub = subscribe(
      path,
      schema,
      (value) => queryClient.setQueryData(queryKey, value),
      (err) => {
        if (import.meta.env.DEV) console.error('[useFirebaseValue]', err);
      },
    );
    return unsub;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, enabled, JSON.stringify(queryKey)]);

  return result;
}
